import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import moment from 'moment';
import { NewHireSettings } from '../../../../models/new-hire';
import { PlanSettings } from '../../../../models/plan';
import { DateUtils } from '../../../../common/utils/date';
import { DatePipe } from '@angular/common';
import {Store} from '@ngrx/store';

@Component({
  standalone: false,
  selector: 'esp-new-hire-class-dialog',
  templateUrl: './new-hire-class-dialog.component.html',
  styleUrls: ['./new-hire-class-dialog.component.css'],
  providers: [DatePipe]
})
export class NewHireClassDialogComponent implements OnInit, OnDestroy {

  public isLoading = false;
  public enableCancel = true;
  public enableAccept = false;
  public formSub: Subscription;
  public startingDateSub: Subscription;
  public endingDateSub: Subscription;

  public addClassForm: FormGroup;
  public managementUnits: any[] = [];
  private _selectedLearningCurve: any = null;
  public selectedMUs: any[] = [];
  private userInfo$: any;
  private locale: any = 'en-US';

  public get selectedLearningCurve(): any {
    return this._selectedLearningCurve;
  }

  public set selectedLearningCurve(value: any) {
    this._selectedLearningCurve = value;
    // Recalculate date constraints when learning curve changes
    if (this.maxDate) {
      this.calculateStartingDateMaxDate();
    }
    if (this.minDate) {
      this.calculateEndingDateMinDate();
    }
    // Recalculate disabled days when learning curve changes (for trainingWeeks = 0 case)
    this.calculateDisabledDays();
    // Recalculate default dates when learning curve changes
    this.calculateDefaultDates();
  }

  public virtualScrollOptions = { itemHeight: 40 };
  public _datesFormat: any;
  public _datePlaceholder: string;
  public isEditMode: boolean = false;
  public existingClassData: any = null;
  public newHireSettings: NewHireSettings;
  public planSettings: PlanSettings;

  // Min and max dates for the calendars
  public minDate: Date | null = null;
  public maxDate: Date | null = null;
  public startingDateMaxDate: Date | null = null;
  public endingDateMinDate: Date | null = null;
  public startingDateDisabledDays: number[] = [];
  public endingDateDisabledDays: number[] = [];
  public startingDateDefaultDate: Date | null = null;
  public endingDateDefaultDate: Date | null = null;
  date: any;

  constructor(
    public activeModal: NgbActiveModal,
    public translate: TranslateService,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private store: Store<any>,
  ) {
    this.addClassForm = this.fb.group({
      managementUnits: [[], [Validators.required]],
      fteCount: ['10', [ Validators.min(0), Validators.max(99999.99)]],
      startingDate: ['', [Validators.required]],
      endingDate: ['', [Validators.required]]
    });

    // Subscribe to form value changes
    this.formSub = this.addClassForm.valueChanges.subscribe(() => {
      this.validateForm();
    });
    this.userInfo$ = this.store.select("state");
    this.userInfo$.subscribe((value: any) => {
      if (value && value.locale) {
        this.locale = value.locale;
      }
    });
  }

  ngOnInit(): void {
    moment.locale((this.translate.currentLang));
    this._datesFormat = this.translate.instant("date.picker.format");
    this._datePlaceholder = this.getDatePlaceholder(this._datesFormat);

    // Set min and max dates based on new hire settings
    this.minDate = this.newHireSettings?.newHireStartDate ? this.createLocalDateFromISOString(this.newHireSettings.newHireStartDate) : null;
    this.maxDate = this.newHireSettings?.newHireEndDate ? this.createLocalDateFromISOString(this.newHireSettings.newHireEndDate) : null;

    // Calculate disabled days based on minDate and maxDate
    this.calculateDisabledDays();

    // Calculate starting date maximum date based on training weeks
    this.calculateStartingDateMaxDate();

    // Calculate ending date minimum date based on training weeks
    this.calculateEndingDateMinDate();

    // Calculate default dates based on current date vs plan date range
    this.calculateDefaultDates();

    // Set up date change subscriptions for automatic calculation
    this.setupDateChangeSubscriptions();

    // Initialize form for edit mode if existing data is provided
    if (this.isEditMode && this.existingClassData) {
      this.initializeEditMode();
    }

    this.validateForm();
  }

  ngOnDestroy(): void {
    if (this.formSub) {
      this.formSub.unsubscribe();
    }
    if (this.startingDateSub) {
      this.startingDateSub.unsubscribe();
    }
    if (this.endingDateSub) {
      this.endingDateSub.unsubscribe();
    }
  }

  validateForm(): void {
    const formValid = this.addClassForm.valid;
    this.enableAccept = formValid;
  }

  submit(): void {
    if (this.enableAccept) {
      const formValue = this.addClassForm.value;

      let selectedOrdered;
      if (this.isEditMode) {
        // In edit mode, create a management unit object from the existing data
        selectedOrdered = [{
          oid: this.existingClassData.muOid,
          label: this.existingClassData.managementUnit
        }];
      } else {
        // In add mode, use the selected management units from the form
        selectedOrdered = this.managementUnits
          .filter(mu => mu.isSelected)
          .map(mu => mu.value);
      }

      // Create the class object - now handling multiple management units
      const startDate = formValue.startingDate instanceof Date ? formValue.startingDate : new Date(formValue.startingDate);
      const endDate = formValue.endingDate instanceof Date ? formValue.endingDate : new Date(formValue.endingDate);

      // Validate dates before converting to ISO string
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        console.error('Invalid date values in form submission');
        return;
      }

      const newClass = {
        managementUnits: selectedOrdered,
        fteCount: formValue.fteCount,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        learningCurveId: this.selectedLearningCurve.learningCurveId
      };

      this.isLoading = true;
      this.enableCancel = false;

      // Close the dialog and return the data
      this.activeModal.close(newClass);
    }
  }

  dismiss(): void {
    this.activeModal.dismiss();
  }

  get isFormValid(): boolean {
    return this.addClassForm.valid;
  }

  get fteCountErrorMessage(): string {
    return this.translate.instant('new.hire.fte.count.validation.error');
  }

  updateSelectedMUs() {
    this.managementUnits.forEach((mu) => {
      mu.isSelected = this.selectedMUs.indexOf(mu.value) != -1;
    })
  }

  private getDatePlaceholder(dateFormat: string): string {
    // Convert date format to uppercase placeholder
    // mm/dd/yy -> MM/DD/YYYY
    // dd/mm/yy -> DD/MM/YYYY
    return dateFormat
      .replace(/mm/g, 'MM')
      .replace(/dd/g, 'DD')
      .replace(/yy/g, 'YYYY');
  }

  private initializeEditMode(): void {
    if (!this.existingClassData) return;

    // Remove the management units control from the form in edit mode
    this.addClassForm.removeControl('managementUnits');

    // Set the form values
    this.addClassForm.patchValue({
      fteCount: this.existingClassData.fteCount,
      startingDate: this.createLocalDateFromISOString(this.existingClassData.startingDate),
      endingDate: this.createLocalDateFromISOString(this.existingClassData.endingDate)
    });
  }

  getManagementUnitDisplayName(): string {
    if (!this.existingClassData) return '';

    // In edit mode, we can use the management unit name directly from the existing data
    return this.existingClassData.managementUnit || '';
  }

  private calculateDefaultDates(): void {
    const currentDate = new Date();
    const planStartDate = this.planSettings?.startDate ? this.createLocalDateFromISOString(this.planSettings.startDate) : null;
    const planEndDate = this.planSettings?.endDate ? this.createLocalDateFromISOString(this.planSettings.endDate) : null;

    // Check if current date is within plan date range
    const isCurrentDateInPlanRange = planStartDate && planEndDate &&
      currentDate >= planStartDate && currentDate <= planEndDate;

    if (isCurrentDateInPlanRange) {
      // Check if current date plus training weeks would exceed plan length
      if (this.selectedLearningCurve?.trainingWeeks && planEndDate) {
        const trainingEndDate = new Date(currentDate);
        trainingEndDate.setDate(trainingEndDate.getDate() + (this.selectedLearningCurve.trainingWeeks * 7) - 1);

        // If training would extend beyond plan end date, fallback to min date
        if (trainingEndDate > planEndDate) {
          this.startingDateDefaultDate = this.minDate;
          this.endingDateDefaultDate = this.minDate;
          return;
        }
      }

      // Set default to current date - the date picker will show the current month
      this.startingDateDefaultDate = currentDate;
      this.endingDateDefaultDate = currentDate;
    } else {
      // Set default to min date
      this.startingDateDefaultDate = this.minDate;
      this.endingDateDefaultDate = this.minDate;
    }
  }

  private calculateDisabledDays(): void {
    // Calculate disabled days for starting date based on minDate
    if (!this.minDate) {
      this.startingDateDisabledDays = [];
    } else {
      const allowedStartingDayOfWeek = this.minDate.getDay();
      this.startingDateDisabledDays = [];
      for (let i = 0; i <= 6; i++) {
        if (i !== allowedStartingDayOfWeek) {
          this.startingDateDisabledDays.push(i);
        }
      }
    }

    // Calculate disabled days for ending date
    // If trainingWeeks is 0, ending date should have same disabled days as starting date
    if (this.selectedLearningCurve?.trainingWeeks === 0) {
      this.endingDateDisabledDays = [...this.startingDateDisabledDays];
    } else {
      // Calculate disabled days for ending date based on maxDate
      if (!this.maxDate) {
        this.endingDateDisabledDays = [];
      } else {
        const allowedEndingDayOfWeek = this.maxDate.getDay();
        this.endingDateDisabledDays = [];
        for (let i = 0; i <= 6; i++) {
          if (i !== allowedEndingDayOfWeek) {
            this.endingDateDisabledDays.push(i);
          }
        }
      }
    }
  }


  private calculateStartingDateMaxDate(): void {
    if (!this.maxDate || !this.selectedLearningCurve?.trainingWeeks) {
      this.startingDateMaxDate = this.maxDate;
      return;
    }

    // Calculate the maximum starting date by subtracting training weeks from maxDate
    const maxStartingDate = new Date(this.maxDate);
    maxStartingDate.setDate(maxStartingDate.getDate() - (this.selectedLearningCurve.trainingWeeks * 7) + 1);

    // Ensure the maximum starting date falls on the correct day of week (minDate day)
    if (this.minDate) {
      const targetDayOfWeek = this.minDate.getDay();
      const currentDayOfWeek = maxStartingDate.getDay();
      const daysDifference = targetDayOfWeek - currentDayOfWeek;

      // Adjust to the previous occurrence of the target day if needed
      if (daysDifference > 0) {
        maxStartingDate.setDate(maxStartingDate.getDate() + daysDifference - 7);
      } else if (daysDifference < 0) {
        maxStartingDate.setDate(maxStartingDate.getDate() + daysDifference);
      }
    }

    this.startingDateMaxDate = maxStartingDate;
  }

  private calculateEndingDateMinDate(): void {
    if (!this.minDate || !this.selectedLearningCurve?.trainingWeeks) {
      this.endingDateMinDate = this.minDate;
      return;
    }

    // Calculate the minimum ending date by adding training weeks to minDate
    const minEndingDate = new Date(this.minDate);
    minEndingDate.setDate(minEndingDate.getDate() + (this.selectedLearningCurve.trainingWeeks * 7) - 1);

    // Ensure the minimum ending date falls on the correct day of week (maxDate day)
    if (this.maxDate) {
      const targetDayOfWeek = this.maxDate.getDay();
      const currentDayOfWeek = minEndingDate.getDay();
      const daysDifference = targetDayOfWeek - currentDayOfWeek;

      // Adjust to the next occurrence of the target day if needed
      if (daysDifference < 0) {
        minEndingDate.setDate(minEndingDate.getDate() + daysDifference + 7);
      } else if (daysDifference > 0) {
        minEndingDate.setDate(minEndingDate.getDate() + daysDifference);
      }
    }

    this.endingDateMinDate = minEndingDate;
  }

  private setupDateChangeSubscriptions(): void {
    // Subscribe to starting date changes
    this.startingDateSub = this.addClassForm.get('startingDate')?.valueChanges.subscribe(value => {
      if (value && this.selectedLearningCurve) {
        this.calculateEndingDateFromStarting(value);
      }
    });

    // Subscribe to ending date changes
    this.endingDateSub = this.addClassForm.get('endingDate')?.valueChanges.subscribe(value => {
      if (value && this.selectedLearningCurve) {
        this.calculateStartingDateFromEnding(value);
      }
    });
  }

  private calculateEndingDateFromStarting(startingDate: Date): void {
    if (!startingDate || !this.selectedLearningCurve) {
      return;
    }

    const start = new Date(startingDate);
    if (isNaN(start.getTime())) {
      return;
    }

    let endingDate: Date;

    // If trainingWeeks is 0, end date should be the same as start date
    if (this.selectedLearningCurve.trainingWeeks === 0) {
      endingDate = new Date(start);
    } else {
      // Calculate ending date by adding trainingWeeks
      endingDate = new Date(start);
      endingDate.setDate(endingDate.getDate() + (this.selectedLearningCurve.trainingWeeks * 7) - 1);

      // Ensure ending date falls on the correct day of week (maxDate day)
      if (this.maxDate) {
        const targetDayOfWeek = this.maxDate.getDay();
        const currentDayOfWeek = endingDate.getDay();
        const daysDifference = targetDayOfWeek - currentDayOfWeek;
        endingDate.setDate(endingDate.getDate() + daysDifference);
      }
    }

    // Update the ending date without triggering its value change subscription
    this.endingDateSub?.unsubscribe();
    this.addClassForm.get('endingDate')?.setValue(endingDate, { emitEvent: false });
    this.setupEndingDateSubscription();
  }

  private calculateStartingDateFromEnding(endingDate: Date): void {
    if (!endingDate || !this.selectedLearningCurve) {
      return;
    }

    const end = new Date(endingDate);
    if (isNaN(end.getTime())) {
      return;
    }

    let startingDate: Date;

    // If trainingWeeks is 0, start date should be the same as end date
    if (this.selectedLearningCurve.trainingWeeks === 0) {
      startingDate = new Date(end);
    } else {
      // Calculate starting date by subtracting trainingWeeks
      startingDate = new Date(end);
      startingDate.setDate(startingDate.getDate() - (this.selectedLearningCurve.trainingWeeks * 7) + 1);

      // Ensure starting date falls on the correct day of week (minDate day)
      if (this.minDate) {
        const targetDayOfWeek = this.minDate.getDay();
        const currentDayOfWeek = startingDate.getDay();
        const daysDifference = targetDayOfWeek - currentDayOfWeek;
        startingDate.setDate(startingDate.getDate() + daysDifference);
      }
    }

    // Update the starting date without triggering its value change subscription
    this.startingDateSub?.unsubscribe();
    this.addClassForm.get('startingDate')?.setValue(startingDate, { emitEvent: false });
    this.setupStartingDateSubscription();
  }

  private setupStartingDateSubscription(): void {
    this.startingDateSub = this.addClassForm.get('startingDate')?.valueChanges.subscribe(value => {
      if (value && this.selectedLearningCurve) {
        this.calculateEndingDateFromStarting(value);
      }
    });
  }

  private setupEndingDateSubscription(): void {
    this.endingDateSub = this.addClassForm.get('endingDate')?.valueChanges.subscribe(value => {
      if (value && this.selectedLearningCurve) {
        this.calculateStartingDateFromEnding(value);
      }
    });
  }

  private createLocalDateFromISOString(isoString: string): Date {
    // Use moment to parse ISO date string as local date to avoid timezone conversion
    // moment.parseZone preserves the date without timezone conversion
    if (!isoString) {
      return new Date();
    }

    // Parse as local date using moment
    // For ISO date strings like "2022-10-25", treat as local date
    const momentDate = moment(isoString.split('T')[0], 'YYYY-MM-DD', true);

    return momentDate.isValid() ? momentDate.toDate() : new Date(isoString);
  }

  formatPlanDate(isoDate: string): string {
    if (!isoDate) {
      return '';
    }

    // Use DateUtils.getShortDate3 which takes datePipe, date, and locale
    // This method uses the environment's dateFormat for the given locale
    return DateUtils.getShortDate3(this.datePipe, isoDate, this.locale);
  }

  get planStartDate(): string {
    return this.planSettings?.startDate ? this.formatPlanDate(this.planSettings.startDate) : '';
  }

  get planEndDate(): string {
    return this.planSettings?.endDate ? this.formatPlanDate(this.planSettings.endDate) : '';
  }
}
